/**
 * ONE TAP — the game loop and the wiring that makes every other module one program.
 *
 * Responsibilities, and the reasoning behind each:
 *
 *  **Fixed-step simulation, free-running render.** The server ticks at TICK_HZ and the
 *  client predicts with the *identical* code, so the client must also step at exactly
 *  TICK_DT or prediction and reconciliation diverge on the first frame. Rendering runs at
 *  display refresh and interpolates, so 144Hz looks like 144Hz while the simulation stays
 *  at 30. MAX_TICKS_PER_FRAME caps the catch-up after a stall — without it, a tab that was
 *  backgrounded for ten seconds would try to simulate three hundred ticks in one frame and
 *  hang the page.
 *
 *  **Two worlds, one truth.** `netWorld` is the authoritative reconstruction: snapshot
 *  deltas are patched into it and it is the thing the interpolator buffers. `world` is
 *  what gets drawn — remote players and guns sampled INTERP_DELAY_MS in the past, with the
 *  local player's body written by the Predictor instead. Prediction is *judged* against
 *  `netWorld` (the newest known round phase), never against the delayed view, or the local
 *  player would sit frozen for a tenth of a second at every round start.
 *
 *  **The render clock is derived, not assumed.** Snapshot timestamps are the server's
 *  *world* clock (milliseconds since the match began), which has nothing to do with wall
 *  time. The offset between that clock and the local one is estimated from the
 *  least-delayed snapshot in a rolling window and slewed, so packet jitter never jerks the
 *  render time — a jerked render clock is visible as every remote player teleporting at
 *  once.
 *
 *  **The scene is a pure function of state.** Rigs, gun meshes and the view model are
 *  created and destroyed by diffing against `world` every frame. Nothing in the simulation
 *  knows a mesh exists.
 */

import * as THREE from 'three';

import {
  FIRE_SHAKE,
  FOV_BASE,
  FOV_MAX,
  FOV_SPEED_REF,
  INTERP_DELAY_MS,
  JUMP_VELOCITY,
  LAND_SHAKE_SCALE,
  MAX_TICKS_PER_FRAME,
  PROTOCOL_VERSION,
  SPEEDLINE_MIN_SPEED,
  TICK_DT,
} from '@shared/constants';
import {
  clamp,
  clamp01,
  copy,
  dampVec,
  dirFromYawPitch,
  lengthXZ,
  lerpN,
  remap,
  rightFromYaw,
  smootherstep,
  v3,
  type Vec3,
} from '@shared/math';
import {
  Btn,
  GunState,
  HealthState,
  RoundPhase,
  type ArenaData,
  type CollisionWorld,
  type GameEvent,
  type InputCommand,
  type LobbyInfo,
  type PlayerState,
  type ServerMessage,
  type WorldState,
} from '@shared/types';
import { ARENA } from '@shared/sim/arena';
import { createCollisionWorld } from '@shared/sim/collision';
import { eyePosition, viewCameraRoll } from '@shared/sim/movement';
import { heldGunOf } from '@shared/sim/gun';

import type { Renderer } from '@client/render/renderer';
import { EVT_REQUEST_LOCK, EVT_UI_SOUND, type UI, type UiSoundKind } from '@client/ui/ui';
// The fixed `UIHooks` surface only carries sensitivity and master volume. Everything else
// the settings panel offers — FOV, quality tier, the music/SFX split, reduced shake, the
// FPS readout — is published through this store instead, and this is the one place in the
// client that subscribes to it and pushes the values into the live subsystems.
import { getSettings, onSettingsChange, type GameSettings } from '@client/ui/settings';
// The spectator readout is a HUD element but its state is owned here, and the fixed
// `UI` surface has no slot for it — so the loop publishes it and the HUD reads it.
import { clearSpectate, publishSpectate, type SpectateMode, type SpectateView } from '@client/ui/hud';
import type { AudioEngine } from '@client/audio/engine';
import { VfxSystem } from '@client/gfx/vfx';
import { ViewModel } from '@client/entities/viewmodel';
import { PlayerRig } from '@client/entities/playerRig';
import { buildArenaMesh } from '@client/entities/arenaMesh';
// `animateGun` is not in the fixed contract, but it is what keeps a chambered gun's idle
// glow pulsing — the single most important readability cue in the game (DESIGN.md §4) —
// and playerRig.ts already drives held guns with it. A world gun must not sit there dead.
import { animateGun, buildGunMesh, disposeGunMesh, styleGun } from '@client/entities/gunModel';
import { Connection, type ConnectionStatus } from '@client/net/connection';
import { Predictor } from '@client/net/prediction';
import {
  SnapshotInterpolator,
  applySnapshot,
  createEmptyWorldState,
} from '@client/net/interpolation';
import {
  ApiError,
  createRoom as apiCreateRoom,
  isValidRoomCode,
  normalizeRoomCode,
  roomCodeFromLocation,
  roomExists,
  roomWsPath,
} from '@client/net/api';
import type { InputManager } from '@client/input/input';
import { describeBinding, type InputCode } from '@client/input/bindings';
import { coerceSensitivity, coerceVolume, sanitizePlayerName, type SettingsStore } from '@client/settings';

// ---------------------------------------------------------------------------
// Loop tuning. Client-side robustness numbers; nothing here alters the simulation.
// ---------------------------------------------------------------------------

/** Longest frame we will believe. Anything above this was a stall, not a slow frame. */
const MAX_FRAME_DT = 0.25;
/**
 * Each input packet repeats the last few commands. The server ignores sequences it has
 * already seen, so this is free insurance against a dropped packet costing a whole tick
 * of movement at the cost of a few dozen bytes.
 */
const COMMAND_REDUNDANCY = 3;
/** Snapshots stop entirely when the server tears the match down; this is how we notice. */
const SNAPSHOT_SILENCE_MS = 2500;
/** Rolling window the render-clock offset estimate is taken over. */
const CLOCK_WINDOW_MS = 2000;
/** Disagreement larger than this is a new match or a tab wake — cut, do not slew. */
const CLOCK_SNAP_MS = 250;
/** Fraction of the remaining clock error corrected per snapshot. */
const CLOCK_SLEW = 0.15;
/** HUD stat cadence. Four updates a second reads as live without being noise. */
const STATS_INTERVAL_MS = 250;
/** Menu backdrop orbit rate, radians/second. Slow enough to be scenery, not motion. */
const MENU_ORBIT_SPEED = 0.055;
/** How long a transient server error message stays on the connection line. */
const ERROR_MESSAGE_MS = 3500;
/**
 * Escape releases the pointer *and* opens the UI's pause panel. Both arrive as separate
 * events with no ordering guarantee, so a lock loss this close to an Escape press is
 * attributed to the pause panel and we stay out of its way.
 */
const PAUSE_ATTRIBUTION_MS = 400;
/**
 * Padding on top of the browser's pointer-lock re-entry cooldown before we retry a request
 * the player already made. Transient user activation outlives this comfortably, so the
 * retry still counts as gesture-initiated.
 */
const LOCK_RETRY_PAD_MS = 80;

// ---------------------------------------------------------------------------
// Death camera + spectator
//
// THE BUG THIS REPLACED: on elimination the camera was left sitting at the corpse,
// wearing whatever pitch the body happened to die with — which, on a fall or a
// headshot, was straight up at the ceiling. Rounds are elimination with no respawn,
// so a dead player could spend most of a round looking at one frozen frame with the
// mouse doing nothing. That is the worst thing in the game and it is not a tuning
// problem: the camera needed a state machine.
//
//   deathcam   ~2s. A smooth pull-back AWAY from the killer, framing the body with
//              the killer beyond it, so the player can read what happened.
//   follow     ride a living teammate, first person or over the shoulder, with mouse
//              look restored and the ability to cycle targets.
//   orbit      nobody is left alive; the round is ending anyway, so the arena turns
//              slowly rather than freezing.
//
// These are camera FRAMING numbers — presentation only, never read by the simulation —
// so they live with the rest of the frame loop's camera constants (MENU_ORBIT_SPEED,
// above) rather than in @shared/constants, which is the sim's contract.
// ---------------------------------------------------------------------------

/** How long the death shot runs. A couple of seconds, not thirty. */
const DEATHCAM_MS = 2000;
/** Metres the camera drifts back, directly away from the killer. */
const DEATHCAM_BACK = 4.2;
/** Metres it rises while doing so. Pitch is DERIVED from these two, so it always looks
 *  down at the body and can never end up pointed at the sky. */
const DEATHCAM_RISE = 1.5;
/** Radius of the sweep that keeps the pull-back from ending inside a wall. */
const DEATHCAM_CLEARANCE = 0.35;
/** A kill event that names us as the victim this long after the camera started still
 *  gets to aim it — snapshots and event batches arrive as separate messages with no
 *  ordering guarantee, so the death can be known a frame before the killer is. */
const DEATHCAM_AIM_GRACE_MS = 400;

/** Over-the-shoulder offsets from the spectated teammate's eye. */
const SPECTATE_DIST = 3.2;
const SPECTATE_RISE = 0.35;
const SPECTATE_SHOULDER = 0.6;
const SPECTATE_CLEARANCE = 0.3;
/** Exponential damp rate for the follow camera. High enough to keep up with a dash,
 *  low enough that the teammate's stride does not shake the frame. */
const SPECTATE_DAMP = 16.0;

/** Radians/second the fallback arena orbit turns. Scenery, not motion. */
const SPECTATE_ORBIT_SPEED = 0.09;
/** How far up the arena's height the orbit rides, and how far out. */
const SPECTATE_ORBIT_RADIUS = 0.52;
const SPECTATE_ORBIT_HEIGHT = 0.62;

const _camEye = v3();
const _camIdeal = v3();
const _camDir = v3();
const _camRight = v3();

/**
 * Movement events the Predictor raises locally. When the server echoes them back for our
 * own body they are duplicates and must be dropped, or every jump plays twice.
 */
const PREDICTED_EVENT_TYPES: ReadonlySet<GameEvent['t']> = new Set([
  'jump',
  'land',
  'slidestart',
  'dash',
  'wallrun',
  'walljump',
]);

const _eye = v3();

/**
 * `RoundPhase` is a `const enum`, so it has no runtime reverse map. The dev overlay wants
 * a readable name, and a hand-written table is cheaper than giving up `const`.
 */
const PHASE_NAMES = ['LOBBY', 'WARMUP', 'LIVE', 'ROUND-END', 'MATCH-END'];

// ---------------------------------------------------------------------------
// Render clock
// ---------------------------------------------------------------------------

/**
 * Estimates the server's *world* clock from snapshot timestamps.
 *
 * Every snapshot gives one sample of `worldTime - localTime`. Network delay only ever
 * makes that sample too small, so the largest sample in a short window is the one that
 * travelled fastest and is therefore the best estimate. Slewing toward it (rather than
 * adopting it) keeps a single fast packet from yanking the render time forward.
 */
class RenderClock {
  private offset = 0;
  private primed = false;
  private lastNow = 0;
  private readonly samples: { at: number; offset: number }[] = [];

  reset(): void {
    this.samples.length = 0;
    this.primed = false;
    this.lastNow = 0;
  }

  get ready(): boolean {
    return this.primed;
  }

  onSnapshot(worldTimeMs: number): void {
    const local = performance.now();
    const sample = worldTimeMs - local;

    // A world clock that went backwards means the server built a new world (the match
    // restarted). Every buffered sample describes a timeline that no longer exists.
    if (this.primed && sample < this.offset - CLOCK_SNAP_MS) {
      this.samples.length = 0;
      this.offset = sample;
      this.lastNow = 0;
      this.samples.push({ at: local, offset: sample });
      return;
    }

    this.samples.push({ at: local, offset: sample });
    while (this.samples.length > 0 && local - this.samples[0].at > CLOCK_WINDOW_MS) this.samples.shift();

    let target = -Infinity;
    for (const s of this.samples) if (s.offset > target) target = s.offset;
    if (!Number.isFinite(target)) return;

    if (!this.primed || Math.abs(target - this.offset) > CLOCK_SNAP_MS) {
      this.offset = target;
      this.primed = true;
      this.lastNow = 0;
    } else {
      this.offset += (target - this.offset) * CLOCK_SLEW;
    }
  }

  /** Estimated server world time, in the same units snapshots are stamped with. */
  now(): number {
    const t = performance.now() + this.offset;
    // Monotonic by construction: a slew that moved the offset backwards must never make
    // the render time go back, because the interpolator would replay a frame.
    if (t < this.lastNow) return this.lastNow;
    this.lastNow = t;
    return t;
  }
}

// ---------------------------------------------------------------------------

export type GameScreen = 'menu' | 'lobby' | 'match';

export interface GameDeps {
  container: HTMLElement;
  renderer: Renderer;
  ui: UI;
  audio: AudioEngine;
  input: InputManager;
  settings: SettingsStore;
}

interface RigEntry {
  rig: PlayerRig;
  team: number;
}

export class Game {
  /** Fired once, after the very first frame has actually been presented. */
  onFirstFrame: () => void = () => {};
  /** Unrecoverable failure. The bootstrap turns this into a visible message. */
  onFatal: (message: string, detail?: unknown) => void = () => {};

  private readonly container: HTMLElement;
  private readonly renderer: Renderer;
  private readonly ui: UI;
  private readonly audio: AudioEngine;
  private readonly input: InputManager;
  private readonly settings: SettingsStore;

  private readonly vfx: VfxSystem;
  private readonly viewmodel: ViewModel;

  private screen: GameScreen = 'menu';
  private disposed = false;
  private firstFrameDone = false;

  // --- net ---------------------------------------------------------------
  private conn: Connection | null = null;
  private connStatus: ConnectionStatus = 'idle';
  private localId = '';
  private lobby: LobbyInfo | null = null;
  private roomCode = '';
  private playerName = '';
  private sawMatchEnd = false;
  private lastSnapshotAtMs = 0;
  private errorMessageUntilMs = 0;

  // --- simulation --------------------------------------------------------
  private arena: ArenaData = ARENA;
  private collision: CollisionWorld = createCollisionWorld(ARENA);
  private predictor: Predictor | null = null;
  private readonly interp = new SnapshotInterpolator();
  private readonly clock = new RenderClock();
  private netWorld: WorldState = createEmptyWorldState();
  private world: WorldState = createEmptyWorldState();
  private seq = 1;
  private accumulator = 0;
  private readonly pendingCmds: InputCommand[] = [];
  private lastLocalHealth: HealthState = HealthState.Dead;
  /**
   * Newest tick seen from the server. A snapshot whose tick went *backwards* is the only
   * unambiguous signal that the room built a brand-new world, which is what lets us tell
   * "the next match started" apart from "a stale packet from the match that just ended".
   */
  private lastSeenTick = -1;
  /** Set when we drop back to the lobby, cleared by the tick-rewind above. */
  private suppressMatch = false;

  // --- death camera + spectator ------------------------------------------
  private specMode: SpectateMode = 'live';
  private specTargetId = '';
  private specFirstPerson = false;
  private specDeathAtMs = 0;
  private readonly specDeathEye = v3();
  /** Yaw pointing from the victim TOWARD the killer. The death shot looks along it. */
  private specKillerYaw = 0;
  private specKillerId = '';
  private specOrbitAngle = 0;
  /** Pending cycle requests, accumulated from button edges between render frames. */
  private specCycle = 0;
  private specTogglePov = false;
  private lastCmdButtons = 0;
  /** Damped follow-camera position; invalid until the first follow frame seeds it. */
  private readonly specCamPos = v3();
  private specCamValid = false;
  /**
   * Published to the HUD every frame we are dead. One instance, mutated in place —
   * the HUD only ever reads fields off it, and a per-frame allocation for a readout
   * that changes twice a round would be waste.
   */
  private readonly specView: SpectateView = {
    mode: 'live',
    targetId: '',
    targetName: '',
    index: 0,
    count: 0,
    firstPerson: false,
    nextKey: '',
    prevKey: '',
    povKey: '',
  };

  // --- scene -------------------------------------------------------------
  private arenaGroup: THREE.Group | null = null;
  private arenaName = '';
  private readonly rigs = new Map<string, RigEntry>();
  private readonly gunMeshes = new Map<number, THREE.Group>();
  private readonly gunStyles = new Map<number, string>();
  private menuAngle = 0;
  /** Monotonic seconds of rendered match time, for looping idle animations. */
  private sceneTimeSec = 0;

  // --- frame -------------------------------------------------------------
  private rafId = 0;
  private lastFrameMs = 0;
  private fps = 60;
  private frameMs = 16.7;
  private statsAtMs = 0;
  private contextLost = false;

  // --- shell DOM ---------------------------------------------------------
  private shellRoot: HTMLDivElement | null = null;
  private shellTitle: HTMLElement | null = null;
  private shellMessage: HTMLElement | null = null;
  private shellKeys: HTMLElement | null = null;
  private shellResume: HTMLButtonElement | null = null;
  private shellLeave: HTMLButtonElement | null = null;
  private devRoot: HTMLDivElement | null = null;
  private devText: HTMLElement | null = null;
  private devVisible = false;
  private shellMode: 'hidden' | 'lock' | 'notice' = 'hidden';

  /**
   * The UI opens its own pause panel on Escape. When it does, this game must not also
   * throw up a click-to-play wall on top of it — one screen, one owner.
   */
  private uiPauseOpen = false;
  private lastEscapeAtMs = -Infinity;
  private lockRetryTimer: ReturnType<typeof setTimeout> | null = null;

  // --- player-facing display settings -------------------------------------
  /** Resting FOV, from the settings panel. FOV_BASE is only the factory default. */
  private fovBase = FOV_BASE;
  /** Camera-trauma multiplier. 0 when the player has asked for reduced shake. */
  private shakeScale = 1;
  private unsubscribeSettings: () => void = () => {};

  private readonly onWindowKeyDown: (e: KeyboardEvent) => void;
  private readonly onVisibilityChange: () => void;
  private readonly onPageHide: () => void;
  private readonly onContextLost: (e: Event) => void;
  private readonly onContextRestored: () => void;
  private readonly onUiSound: (e: Event) => void;
  private readonly onUiRequestLock: () => void;
  private readonly onCanvasPointerDown: () => void;

  constructor(deps: GameDeps) {
    this.container = deps.container;
    this.renderer = deps.renderer;
    this.ui = deps.ui;
    this.audio = deps.audio;
    this.input = deps.input;
    this.settings = deps.settings;

    this.vfx = new VfxSystem(this.renderer.scene);
    // The VfxSystem is constructed with a scene and nothing else, so the two effects it
    // cannot produce on its own — camera trauma and the full-screen ink splat — are
    // bridged back to the Renderer here. Without this every headshot, disarm, wound,
    // grapple catch, round wipe and kill frame silently loses its screen-space kick.
    this.vfx.hooks = {
      shake: (amount) => this.addShake(amount),
      inkSplash: (colorHex) => this.renderer.inkSplash(colorHex),
    };

    this.viewmodel = new ViewModel();
    this.viewmodel.object.visible = false;
    this.renderer.camera.add(this.viewmodel.object);

    this.playerName = this.settings.get().playerName;

    this.setArena(ARENA);
    this.buildShell();

    // --- input plumbing --------------------------------------------------
    this.input.onLockChange = (locked) => this.handleLockChange(locked);
    this.input.onLockBlocked = (reason, retryInMs) => this.handleLockBlocked(reason, retryInMs);
    this.input.onAction = (action, down) => this.handleInputAction(action, down);
    this.input.onAnyInput = () => this.wakeAudio();

    // --- lifecycle listeners ---------------------------------------------
    this.onWindowKeyDown = (e) => {
      if (e.code === 'F3') {
        e.preventDefault();
        this.setDevOverlay(!this.devVisible);
        return;
      }
      if (e.code === 'Escape' && this.screen === 'match') {
        // The UI turns this into its pause panel. Remember it so the lock-loss that the
        // browser fires alongside does not get mistaken for an alt-tab.
        this.lastEscapeAtMs = performance.now();
        this.uiPauseOpen = true;
        this.hideShell();
      }
    };
    this.onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Coming back from a background tab: the accumulator and the render clock both
        // describe a world that stopped existing while we were away.
        this.lastFrameMs = performance.now();
        this.accumulator = 0;
        this.clock.reset();
      }
    };
    this.onPageHide = () => {
      this.settings.flush();
    };
    this.onContextLost = (e) => {
      e.preventDefault();
      this.contextLost = true;
      this.showNotice('GRAPHICS PAUSED', 'The graphics context was lost. Restoring…', false);
    };
    this.onContextRestored = () => {
      this.contextLost = false;
      this.renderer.resize();
      if (this.shellMode === 'notice') this.hideShell();
      if (this.screen === 'match' && !this.input.isPointerLocked()) this.showLockPrompt();
    };
    // The UI has no slot in the fixed `UIHooks` for these two, so it announces them on
    // `window` instead (see ui.ts). Every button press in the menus arrives here.
    this.onUiSound = (e) => {
      const kind = (e as CustomEvent<{ kind?: UiSoundKind }>).detail?.kind;
      if (kind) this.audio.ui(kind);
    };
    this.onUiRequestLock = () => {
      this.uiPauseOpen = false;
      if (this.screen === 'match') this.acquireLock();
    };

    window.addEventListener('keydown', this.onWindowKeyDown);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pagehide', this.onPageHide);
    this.renderer.gl.domElement.addEventListener('webglcontextlost', this.onContextLost);
    this.renderer.gl.domElement.addEventListener('webglcontextrestored', this.onContextRestored);
    // Catch-all: the UI layer is pointer-events:none except where it is interactive, so a
    // click that actually reaches the canvas during a match means "put me back in".
    this.onCanvasPointerDown = () => {
      if (this.screen !== 'match' || this.input.isPointerLocked()) return;
      this.uiPauseOpen = false;
      this.resumeFromShell();
    };

    window.addEventListener(EVT_UI_SOUND, this.onUiSound);
    window.addEventListener(EVT_REQUEST_LOCK, this.onUiRequestLock);
    this.renderer.gl.domElement.addEventListener('pointerdown', this.onCanvasPointerDown);

    this.setDevOverlay(this.settings.get().showStats);

    // Display settings, applied now and on every change from the settings panel.
    this.applyDisplaySettings(getSettings());
    this.unsubscribeSettings = onSettingsChange((s) => this.applyDisplaySettings(s));
  }

  // =========================================================================
  // Display settings
  // =========================================================================

  /**
   * Push the settings panel's values into the subsystems that own them.
   *
   * Quality is forwarded through `SettingsStore` rather than straight at the renderer so
   * the two stores cannot disagree about which tier is live — the store is what re-applies
   * the tier after a context loss, and it also owns the auto-quality watchdog toggle.
   */
  private applyDisplaySettings(s: Readonly<GameSettings>): void {
    this.fovBase = s.fov;
    this.shakeScale = s.reducedShake ? 0 : 1;
    this.audio.setMusicVolume(s.musicVolume);
    this.audio.setSfxVolume(s.sfxVolume);
    if (this.settings.get().quality !== s.quality) this.settings.patch({ quality: s.quality });
    if (this.devVisible !== s.showFps) this.setDevOverlay(s.showFps);
  }

  /**
   * Every camera kick in the game funnels through here so "reduced shake" is a single
   * switch rather than a flag each of a dozen call sites has to remember.
   */
  private addShake(amount: number): void {
    if (this.shakeScale <= 0) return;
    this.renderer.addShake(amount * this.shakeScale);
  }

  // =========================================================================
  // Lifecycle
  // =========================================================================

  /** Show the menu and start the frame loop. */
  start(): void {
    if (this.disposed) return;
    this.screen = 'menu';
    this.ui.showMenu();
    this.ui.setConnection('idle');
    this.lastFrameMs = performance.now();
    this.rafId = requestAnimationFrame(this.frame);

    // A shared invite link should land the player in the room, not in the menu — but only
    // if we already know what to call them. Otherwise the menu asks first.
    const code = roomCodeFromLocation();
    if (code && this.playerName) void this.joinRoom(code, this.playerName);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.rafId !== 0) cancelAnimationFrame(this.rafId);
    this.rafId = 0;

    window.removeEventListener('keydown', this.onWindowKeyDown);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pagehide', this.onPageHide);
    this.renderer.gl.domElement.removeEventListener('webglcontextlost', this.onContextLost);
    this.renderer.gl.domElement.removeEventListener('webglcontextrestored', this.onContextRestored);
    window.removeEventListener(EVT_UI_SOUND, this.onUiSound);
    window.removeEventListener(EVT_REQUEST_LOCK, this.onUiRequestLock);
    this.renderer.gl.domElement.removeEventListener('pointerdown', this.onCanvasPointerDown);
    if (this.lockRetryTimer !== null) clearTimeout(this.lockRetryTimer);
    this.lockRetryTimer = null;
    this.unsubscribeSettings();
    this.unsubscribeSettings = () => {};

    this.closeConnection();
    this.clearMatchScene();

    this.renderer.camera.remove(this.viewmodel.object);
    this.vfx.dispose();

    // Gun materials are minted per instance (the amber chamber emissive is per gun), so
    // every owner has to free its own. `disposeGunMesh` is careful to leave the shared
    // toon gradient ramp alone.
    for (const mesh of this.gunMeshes.values()) {
      this.renderer.scene.remove(mesh);
      disposeGunMesh(mesh);
    }
    this.gunMeshes.clear();
    this.gunStyles.clear();

    if (this.arenaGroup) {
      this.renderer.scene.remove(this.arenaGroup);
      this.disposeArenaGroup(this.arenaGroup);
      this.arenaGroup = null;
    }

    this.shellRoot?.remove();
    this.devRoot?.remove();
    this.shellRoot = null;
    this.devRoot = null;
  }

  // =========================================================================
  // UI hook surface — main.ts forwards `UIHooks` straight into these.
  // =========================================================================

  async createRoom(name: string): Promise<void> {
    this.wakeAudio();
    const player = this.rememberName(name);
    this.ui.setConnection('connecting', 'Creating room…');
    let code: string;
    try {
      code = await apiCreateRoom();
    } catch (err) {
      this.reportConnectionFailure(err, 'Could not create a room.');
      return;
    }
    await this.connect(code, player);
  }

  async joinRoom(code: string, name: string): Promise<void> {
    this.wakeAudio();
    const player = this.rememberName(name);
    const normalized = normalizeRoomCode(code);
    if (!isValidRoomCode(normalized)) {
      this.audio.ui('error');
      this.ui.setConnection('error', 'That room code is not valid.');
      return;
    }

    this.ui.setConnection('connecting', `Looking for ${normalized}…`);
    let exists = false;
    try {
      exists = await roomExists(normalized);
    } catch (err) {
      this.reportConnectionFailure(err, 'Could not reach the server.');
      return;
    }
    if (!exists) {
      this.audio.ui('error');
      this.ui.setConnection('error', `No room called ${normalized}.`);
      return;
    }

    await this.connect(normalized, player);
  }

  setReady(ready: boolean): void {
    this.audio.ui(ready ? 'ready' : 'click');
    this.conn?.send({ t: 'ready', ready });
  }

  switchTeam(team: number): void {
    this.audio.ui('click');
    this.conn?.send({ t: 'switchteam', team });
  }

  startMatch(): void {
    this.audio.ui('ready');
    this.conn?.send({ t: 'startmatch' });
  }

  setBots(fill: boolean): void {
    this.audio.ui('click');
    this.conn?.send({ t: 'setbots', fill });
  }

  /** Back to the main menu from anywhere. */
  leave(): void {
    this.audio.ui('back');
    this.closeConnection();
    this.clearMatchScene();
    this.lobby = null;
    this.localId = '';
    this.roomCode = '';
    this.screen = 'menu';
    this.suppressMatch = false;
    this.lastSeenTick = -1;
    this.uiPauseOpen = false;
    this.cancelLockRetry();
    this.input.enabled = false;
    this.input.releaseLock();
    this.hideShell();
    this.clearLocationCode();
    this.ui.showMenu();
    this.ui.setConnection('idle');
  }

  setSensitivity(v: number): void {
    this.settings.patch({ sensitivity: coerceSensitivity(v) });
  }

  setVolume(v: number): void {
    this.settings.patch({ volume: coerceVolume(v) });
  }

  // =========================================================================
  // Connection
  // =========================================================================

  private rememberName(name: string): string {
    const clean = sanitizePlayerName(name) || this.playerName || 'PLAYER';
    this.playerName = clean;
    this.settings.patch({ playerName: clean });
    return clean;
  }

  private reportConnectionFailure(err: unknown, fallback: string): void {
    const message = err instanceof ApiError ? err.message : fallback;
    this.audio.ui('error');
    this.ui.setConnection('error', message);
  }

  private async connect(code: string, name: string): Promise<void> {
    this.closeConnection();
    this.clearMatchScene();

    this.roomCode = code;
    const conn = new Connection(roomWsPath(code));
    this.conn = conn;

    conn.onStatus = (status, detail) => {
      if (this.conn !== conn) return;
      this.connStatus = status;
      // A server-sent error message owns the status line for a moment; don't stomp it.
      if (performance.now() < this.errorMessageUntilMs && status === 'connected') return;
      this.ui.setConnection(status, detail);
      if (status === 'error') {
        this.audio.ui('error');
        this.returnToMenuOnFailure(detail ?? 'Connection lost.');
      }
    };
    conn.onClose = () => {
      if (this.conn !== conn) return;
      // Connection retries on its own and the status callback narrates it. The snapshot
      // buffer is deliberately left alone — it holds the last good view of the arena, and
      // freezing on that is far better than emptying the world. Only the clock is dropped,
      // because local time keeps running while the server's world time does not.
      this.clock.reset();
    };
    conn.onMessage = (m) => {
      if (this.conn !== conn) return;
      this.handleMessage(m);
    };

    this.ui.setConnection('connecting', `Joining ${code}…`);
    try {
      await conn.connect();
    } catch (err) {
      if (this.conn !== conn) return;
      const message = err instanceof Error ? err.message : 'Could not connect.';
      this.audio.ui('error');
      this.ui.setConnection('error', message);
      this.conn = null;
      return;
    }
    if (this.conn !== conn) return;

    conn.send({ t: 'hello', name, protocol: PROTOCOL_VERSION });
    this.setLocationCode(code);
  }

  private closeConnection(): void {
    const conn = this.conn;
    this.conn = null;
    if (!conn) return;
    conn.onMessage = () => {};
    conn.onClose = () => {};
    conn.onStatus = () => {};
    conn.close();
  }

  private returnToMenuOnFailure(message: string): void {
    if (this.screen === 'menu') return;
    this.closeConnection();
    this.clearMatchScene();
    this.screen = 'menu';
    this.suppressMatch = false;
    this.lastSeenTick = -1;
    this.uiPauseOpen = false;
    this.cancelLockRetry();
    this.input.enabled = false;
    this.input.releaseLock();
    this.hideShell();
    this.ui.showMenu();
    this.ui.setConnection('error', message);
  }

  private setLocationCode(code: string): void {
    try {
      if (location.hash.replace(/^#/, '') !== code) history.replaceState(null, '', `#${code}`);
    } catch {
      /* history is unavailable in some embedded contexts; the game does not need it */
    }
  }

  private clearLocationCode(): void {
    try {
      if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    } catch {
      /* ignore */
    }
  }

  // =========================================================================
  // Server messages
  // =========================================================================

  private handleMessage(m: ServerMessage): void {
    switch (m.t) {
      case 'welcome': {
        this.localId = m.id;
        this.lobby = m.lobby;
        this.setArena(m.arena);
        this.predictor = new Predictor(this.collision);
        this.seq = 1;
        this.pendingCmds.length = 0;
        this.sawMatchEnd = false;
        this.enterLobby(false);
        return;
      }

      case 'lobby': {
        this.lobby = m.lobby;
        if (this.screen === 'lobby') this.ui.showLobby(m.lobby, this.localId);
        // The room broadcasts its lobby the instant it tears a finished match down. We
        // only treat that as "the match is over" once we have actually seen MatchEnd,
        // because a player joining mid-round also triggers a lobby broadcast.
        else if (this.screen === 'match' && this.sawMatchEnd) this.enterLobby(true);
        return;
      }

      case 'snapshot': {
        const restarted = m.tick < this.lastSeenTick;
        this.lastSeenTick = m.tick;
        if (restarted) this.suppressMatch = false;
        // Trailing snapshots from a match the room has already torn down would otherwise
        // drag us straight back into a game that no longer exists.
        if (this.suppressMatch) return;

        this.lastSnapshotAtMs = performance.now();
        applySnapshot(this.netWorld, m.tick, m.timeMs, m.state);
        this.interp.push(m.tick, m.timeMs, this.netWorld);
        this.clock.onSnapshot(m.timeMs);

        if (this.screen !== 'match') this.enterMatch();
        if (this.netWorld.match.phase === RoundPhase.MatchEnd) this.sawMatchEnd = true;

        const serverLocal = this.netWorld.players[this.localId];
        const local = this.world.players[this.localId];
        if (serverLocal && local && this.predictor) {
          this.adoptSpawnView(serverLocal);
          this.predictor.reconcile(serverLocal, m.ackSeq, local, this.netWorld);
        } else if (serverLocal) {
          this.adoptSpawnView(serverLocal);
        }
        return;
      }

      case 'events': {
        for (const e of m.events) {
          // Our own movement was already played by prediction on the frame it happened.
          if (PREDICTED_EVENT_TYPES.has(e.t) && this.eventPlayer(e) === this.localId) continue;
          this.routeEvent(e);
        }
        return;
      }

      case 'error': {
        this.audio.ui('error');
        this.errorMessageUntilMs = performance.now() + ERROR_MESSAGE_MS;
        this.ui.setConnection('error', m.message);
        window.setTimeout(() => {
          if (performance.now() >= this.errorMessageUntilMs && this.conn) {
            this.ui.setConnection(this.connStatus);
          }
        }, ERROR_MESSAGE_MS);
        return;
      }

      case 'kick': {
        this.audio.ui('error');
        this.returnToMenuOnFailure(m.reason);
        return;
      }

      case 'chat':
      case 'pong':
        return;

      default:
        return;
    }
  }

  /** The player an event is "about", for local-duplicate suppression. */
  private eventPlayer(e: GameEvent): string | null {
    return 'player' in e && typeof e.player === 'string' ? e.player : null;
  }

  /**
   * The server decides which way you face when a round starts. The mouse owns the camera
   * every other instant, so the only honest moment to adopt the server's facing is the
   * transition from eliminated to alive.
   */
  private adoptSpawnView(serverLocal: PlayerState): void {
    const now = serverLocal.healthState;
    if (this.lastLocalHealth === HealthState.Dead && now !== HealthState.Dead) {
      this.input.setView(serverLocal.yaw, serverLocal.pitch);
    }
    this.lastLocalHealth = now;
  }

  // =========================================================================
  // Screens
  // =========================================================================

  /**
   * `afterMatch` distinguishes "the room just tore a finished match down" from "we have
   * only just joined". In the first case trailing snapshots from the dead match are still
   * in flight and must be ignored until the room builds a new world; in the second, the
   * very next snapshot may be a match already in progress and has to be taken seriously.
   */
  private enterLobby(afterMatch: boolean): void {
    this.clearMatchScene();
    this.screen = 'lobby';
    this.sawMatchEnd = false;
    this.suppressMatch = afterMatch;
    if (!afterMatch) this.lastSeenTick = -1;
    this.uiPauseOpen = false;
    this.cancelLockRetry();
    this.input.enabled = false;
    this.input.releaseLock();
    this.hideShell();
    if (this.lobby) this.ui.showLobby(this.lobby, this.localId);
  }

  private enterMatch(): void {
    this.screen = 'match';
    this.sawMatchEnd = false;
    this.suppressMatch = false;
    this.uiPauseOpen = false;
    this.cancelLockRetry();
    this.accumulator = 0;
    this.lastLocalHealth = HealthState.Dead;
    this.predictor?.reset();
    this.ui.showGame();
    this.input.enabled = true;
    this.showLockPrompt();
  }

  // =========================================================================
  // Frame
  // =========================================================================

  private readonly frame = (nowMs: number): void => {
    if (this.disposed) return;
    this.rafId = requestAnimationFrame(this.frame);

    const raw = (nowMs - this.lastFrameMs) / 1000;
    this.lastFrameMs = nowMs;
    const dt = clamp(Number.isFinite(raw) ? raw : 0, 0, MAX_FRAME_DT);

    this.frameMs = this.frameMs + (dt * 1000 - this.frameMs) * 0.1;
    this.fps = this.frameMs > 0 ? 1000 / this.frameMs : 0;

    this.input.beginFrame(dt);

    // The tick runs whenever we are seated in a room, not only during a match. Outside a
    // match `sample()` produces a neutral command, but the server needs the packets: its
    // round-trip estimate — the ping every player reads in the lobby — is derived from the
    // timestamps on the input stream and from nothing else.
    if (this.conn && this.localId) this.stepSimulation(dt);
    if (this.screen === 'match') this.detectMatchTeardown();

    this.renderFrame(dt);
    this.updateStats(nowMs);

    if (!this.firstFrameDone) {
      this.firstFrameDone = true;
      const cb = this.onFirstFrame;
      this.onFirstFrame = () => {};
      cb();
    }
  };

  /** Fixed-step accumulator. The only place the simulation clock advances. */
  private stepSimulation(dt: number): void {
    this.accumulator += dt;

    // Spiral-of-death guard: never try to make up more than one frame's worth of ticks.
    const ceiling = TICK_DT * MAX_TICKS_PER_FRAME;
    if (this.accumulator > ceiling) this.accumulator = ceiling;

    let ticks = 0;
    while (this.accumulator >= TICK_DT && ticks < MAX_TICKS_PER_FRAME) {
      this.tick();
      this.accumulator -= TICK_DT;
      ticks++;
    }
  }

  private tick(): void {
    const conn = this.conn;
    if (!conn || !this.localId) return;

    // The server rewinds hitboxes to the render time this command was aimed at, expressed
    // on the server's own wall clock (see src/worker/room.ts `updateLatency`).
    const clientTimeMs = Math.round(conn.serverNowMs() - INTERP_DELAY_MS);
    const cmd = this.input.sample(this.seq++, TICK_DT, clientTimeMs);

    this.pendingCmds.push(cmd);
    while (this.pendingCmds.length > COMMAND_REDUNDANCY) this.pendingCmds.shift();
    conn.send({ t: 'input', cmds: this.pendingCmds.slice() });

    // Spectator controls ride the same button stream as everything else — there is no
    // separate dead-player input path, and adding one would mean a binding the player
    // could not see or rebind. They are read as EDGES so a held mouse button does not
    // spin through the roster. The edge state is tracked while alive too, or the first
    // press after dying would fire on whatever was already down at the moment of death.
    const pressed = cmd.buttons & ~this.lastCmdButtons;
    this.lastCmdButtons = cmd.buttons;
    if (this.specMode !== 'live') {
      if ((pressed & Btn.Fire) !== 0) this.specCycle += 1;
      if ((pressed & Btn.Throw) !== 0) this.specCycle -= 1;
      if ((pressed & Btn.Jump) !== 0) this.specTogglePov = true;
    }

    const predictor = this.predictor;
    const local = this.world.players[this.localId];
    if (!predictor || !local) return;

    predictor.pushInput(cmd, local, this.netWorld);

    if (predictor.events.length > 0) {
      for (const e of predictor.events) this.routeEvent(e);
      predictor.events.length = 0;
    }
  }

  /**
   * The room stops sending snapshots the moment it destroys the world. Combined with the
   * lobby broadcast that accompanies it this is belt and braces, but the silence check is
   * the one that survives a lost packet.
   */
  private detectMatchTeardown(): void {
    if (this.connStatus !== 'connected') return;
    if (this.lastSnapshotAtMs === 0) return;
    if (performance.now() - this.lastSnapshotAtMs < SNAPSHOT_SILENCE_MS) return;
    this.lastSnapshotAtMs = 0;
    if (this.lobby) this.enterLobby(true);
  }

  private renderFrame(dt: number): void {
    // A lost context has no valid GPU resources; drawing into it is undefined behaviour.
    if (this.contextLost) return;

    if (this.screen === 'match') {
      // While the socket is down the clock stops being trustworthy and the buffer stops
      // being fed. Freezing the scene where it stands reads as lag, which is the truth;
      // falling back to the menu orbit would yank the camera out of the player's hands.
      if (this.clock.ready && this.interp.latest() !== null) {
        const renderTimeMs = this.clock.now() - INTERP_DELAY_MS;
        this.interp.sample(renderTimeMs, this.world, this.localId);
        // Order matters: the spectator decides whose body the camera is inside, and
        // `syncScene` has to hide that body on the same frame the camera moves into it.
        this.updateSpectate();
        this.syncScene(dt);
        this.updateCamera(dt);
      }
      this.ui.updateHud(this.world, this.localId, dt);
    } else {
      this.updateMenuCamera(dt);
    }

    this.vfx.update(dt, this.world, this.localId);
    this.audio.update(this.world, this.localId, dt);
    this.renderer.render(dt);
  }

  // =========================================================================
  // Camera
  // =========================================================================

  /**
   * The one body the camera is inside, which must never be drawn or it fills the frame.
   *
   * Note what this deliberately does NOT hide: the corpse during the death shot and the
   * spectated teammate over the shoulder. Both are the subject of the picture.
   */
  private hiddenRigId(): string {
    if (this.specMode === 'live') return this.localId;
    if (this.specMode === 'follow' && this.specFirstPerson) return this.specTargetId;
    return '';
  }

  /** Living teammates, in a stable order, as the spectate cycle sees them. */
  private livingTeammates(local: PlayerState): PlayerState[] {
    const out: PlayerState[] = [];
    for (const id in this.world.players) {
      const p = this.world.players[id];
      if (p.team !== local.team || p.id === local.id) continue;
      if (p.healthState === HealthState.Dead || !p.connected) continue;
      out.push(p);
    }
    // Sorted by id, not by iteration order: the cycle must land on the same player in
    // the same place every frame, or pressing "next" would be a lottery.
    out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    return out;
  }

  /**
   * The death/spectate state machine. Runs once per rendered frame, before the camera.
   *
   * Everything it decides is derived from the world plus elapsed time, so there is no
   * way for it to latch: the instant the local player is alive again it drops straight
   * back to `live` and hands the camera back to the mouse.
   */
  private updateSpectate(): void {
    const local = this.world.players[this.localId];
    if (!local || local.healthState !== HealthState.Dead) {
      if (this.specMode !== 'live') this.exitSpectate();
      return;
    }

    if (this.specMode === 'live') this.beginDeathCam(local);

    const mates = this.livingTeammates(local);

    if (this.specMode === 'deathcam') {
      // The shot runs its full length even if everyone dies during it — cutting away
      // from your own death after 300ms is worse than watching it.
      if (performance.now() - this.specDeathAtMs < DEATHCAM_MS) {
        this.specCycle = 0;
        this.specTogglePov = false;
        this.publishSpectateView(0, 0);
        return;
      }
      this.specMode = mates.length > 0 ? 'follow' : 'orbit';
      this.specTargetId = '';
      this.specCamValid = false;
      // Re-read the bindings on the frame the key caps first become meaningful.
      // They were also read at the moment of death, but the death shot is two
      // seconds during which the player can open the pause panel and rebind, and
      // a key cap that lies is worse than no key cap.
      this.cacheSpectateKeys();
    }

    if (mates.length === 0) {
      this.specMode = 'orbit';
      this.specTargetId = '';
      this.specCycle = 0;
      this.specTogglePov = false;
      this.publishSpectateView(0, 0);
      return;
    }

    this.specMode = 'follow';
    let index = mates.findIndex((p) => p.id === this.specTargetId);
    if (index < 0) {
      // No target, or the one we had just died. Either way this is a cut, so the
      // free-look basis is re-seeded from where the new target is actually looking.
      index = 0;
      this.adoptSpectateTarget(mates[0]!);
    }
    if (this.specCycle !== 0) {
      const n = mates.length;
      index = (((index + this.specCycle) % n) + n) % n;
      this.adoptSpectateTarget(mates[index]!);
      this.specCycle = 0;
    }
    if (this.specTogglePov) {
      this.specFirstPerson = !this.specFirstPerson;
      this.specTogglePov = false;
      this.specCamValid = false;
    }

    this.publishSpectateView(index + 1, mates.length);
  }

  /** Enter the death shot. Captures the pose to frame, not the pose to inherit. */
  private beginDeathCam(local: PlayerState): void {
    eyePosition(local, this.specDeathEye);
    this.specDeathAtMs = performance.now();
    this.specMode = 'deathcam';
    this.specTargetId = '';
    this.specFirstPerson = false;
    this.specCycle = 0;
    this.specTogglePov = false;
    this.specCamValid = false;
    // The victim's own yaw is the fallback: a fall or a void death has no killer, and
    // looking the way you were running is the honest reading of what happened. The
    // victim's PITCH is never inherited — that is the bug this whole path replaced.
    this.specKillerYaw = local.yaw;
    this.aimDeathCam(local);
    this.cacheSpectateKeys();
  }

  /** Point the death shot at the killer, if we know who that is by now. */
  private aimDeathCam(local: PlayerState): void {
    const killer = this.specKillerId ? this.world.players[this.specKillerId] : undefined;
    if (!killer) return;
    const dx = killer.pos.x - local.pos.x;
    const dz = killer.pos.z - local.pos.z;
    if (dx * dx + dz * dz < 1e-4) return;
    // Yaw whose forward vector (-sin, -cos) points at the killer.
    this.specKillerYaw = Math.atan2(-dx, -dz);
  }

  /**
   * Recorded when a kill event names us as the victim.
   *
   * Snapshots and event batches are separate messages, so the health flip and the kill
   * event can arrive in either order. If the camera has already started, it re-aims —
   * within a short grace window, so a later round's kill can never move an old shot.
   */
  private noteLocalDeath(killerId: string): void {
    this.specKillerId = killerId === this.localId ? '' : killerId;
    if (this.specMode !== 'deathcam') return;
    if (performance.now() - this.specDeathAtMs > DEATHCAM_AIM_GRACE_MS) return;
    const local = this.world.players[this.localId];
    if (local) this.aimDeathCam(local);
  }

  /**
   * Switch to a new spectate target. This is a CUT, so the mouse basis is re-seeded
   * from the target's own facing — otherwise free-look would start pointing wherever
   * the player happened to leave it, at a body that is somewhere else entirely.
   */
  private adoptSpectateTarget(p: PlayerState): void {
    this.specTargetId = p.id;
    this.specCamValid = false;
    // Over the shoulder the pitch starts LEVEL rather than inheriting theirs: adopting a
    // teammate who happens to be looking at their own feet would seat the camera in the
    // floor. In first person you want exactly what they are looking at, so take it.
    this.input.setView(p.yaw, this.specFirstPerson ? p.pitch : 0);
  }

  /** Drop every trace of the spectator and hand the camera back. */
  private exitSpectate(): void {
    this.specMode = 'live';
    this.specTargetId = '';
    this.specKillerId = '';
    this.specFirstPerson = false;
    this.specCycle = 0;
    this.specTogglePov = false;
    this.specCamValid = false;
    // Roll and FOV are damped toward whatever the live camera asks for on the very
    // next frame, so there is nothing left over to unwind. `adoptSpawnView` has
    // already reset yaw and pitch from the server's spawn facing.
    this.renderer.setCameraRoll(0);
    clearSpectate();
    this.specView.mode = 'live';
  }

  /**
   * Key labels for the spectator bar, read from the live bindings rather than hard-coded
   * so a rebound key can never be advertised wrong. Re-read on entering the death shot
   * and again on entering follow/orbit, which is every transition after which the caps
   * are actually on screen — not per frame, because `describeBinding` builds strings and
   * this file's performance contract does not spend allocations on a readout that
   * changes twice a round.
   *
   * Only the PRIMARY binding: `describeBinding` joins every alternative with a slash,
   * which is right for the pause panel's crib sheet and three times too wide for a key
   * cap on a bar the player reads in half a second.
   */
  private cacheSpectateKeys(): void {
    const b = this.input.bindings;
    this.specView.nextKey = primaryKey(b.fire);
    this.specView.prevKey = primaryKey(b.throwGun);
    this.specView.povKey = primaryKey(b.jump);
  }

  private publishSpectateView(index: number, count: number): void {
    const v = this.specView;
    v.mode = this.specMode;
    v.targetId = this.specTargetId;
    v.targetName = (this.world.players[this.specTargetId]?.name ?? '').toUpperCase();
    v.index = index;
    v.count = count;
    v.firstPerson = this.specFirstPerson;
    publishSpectate(v);
  }

  private updateCamera(dt: number): void {
    switch (this.specMode) {
      case 'deathcam':
        this.updateDeathCam();
        return;
      case 'follow':
        this.updateFollowCam(dt);
        return;
      case 'orbit':
        this.updateSpectateOrbit(dt);
        return;
      default:
        break;
    }

    const p = this.world.players[this.localId];
    if (!p) return;

    const yaw = this.input.yaw;
    const pitch = this.input.pitch;

    eyePosition(p, _eye);
    const cam = this.renderer.camera;
    cam.position.set(_eye.x, _eye.y, _eye.z);
    cam.rotation.set(pitch, yaw, 0);

    this.renderer.setCameraRoll(viewCameraRoll(p));

    // Feeling fast is a rendering job (DESIGN.md §3): FOV and the speed-line/chromatic
    // channel both ride actual horizontal velocity, not the player's speed *cap*.
    const speed = lengthXZ(p.vel);
    // The speed push is a fixed *offset* above the player's chosen resting FOV, not a
    // ramp toward a fixed ceiling — otherwise picking a wide FOV would quietly delete the
    // feedback, and picking a narrow one would exaggerate it.
    const fovPush = FOV_MAX - FOV_BASE;
    this.renderer.setTargetFov(
      lerpN(this.fovBase, this.fovBase + fovPush, clamp01(speed / FOV_SPEED_REF)),
    );
    this.renderer.setSpeed01(remap(speed, SPEEDLINE_MIN_SPEED, FOV_SPEED_REF, 0, 1));

    this.audio.setListener(_eye, yaw, pitch);
  }

  /**
   * THE DEATH SHOT. Two seconds, one move: the camera drifts back along the line to the
   * killer and rises, framing the body with whoever shot it beyond.
   *
   * Its pitch is DERIVED from how far it has risen and pulled back — `atan2(rise, back)`
   * — so it is looking at the death point by construction. There is no path through this
   * function that produces an upward pitch, which was the entire failure mode before.
   */
  private updateDeathCam(): void {
    const t = clamp01((performance.now() - this.specDeathAtMs) / DEATHCAM_MS);
    const e = smootherstep(t);
    const back = DEATHCAM_BACK * e;
    const rise = DEATHCAM_RISE * e;

    const yaw = this.specKillerYaw;
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);

    _camIdeal.x = this.specDeathEye.x - fx * back;
    _camIdeal.y = this.specDeathEye.y + rise;
    _camIdeal.z = this.specDeathEye.z - fz * back;

    // A rooftop arena has plant rooms, parapets and stair heads right where a body
    // falls, so the pull-back is swept rather than teleported. Ending inside a wall
    // would put the player inside a black polygon, which is the old bug in a hat.
    // `startSolid` means the corpse's own eye is inside geometry, in which case the
    // sweep reports fraction 0 and clamping to it would pin the camera to the body —
    // which is the frozen frame this whole path exists to delete. Better a couple of
    // metres of clipped wall than that.
    const hit = this.collision.traceSphere(this.specDeathEye, _camIdeal, DEATHCAM_CLEARANCE);
    if (hit.hit && !hit.startSolid) {
      const f = clamp01(hit.fraction);
      _camIdeal.x = this.specDeathEye.x + (_camIdeal.x - this.specDeathEye.x) * f;
      _camIdeal.y = this.specDeathEye.y + (_camIdeal.y - this.specDeathEye.y) * f;
      _camIdeal.z = this.specDeathEye.z + (_camIdeal.z - this.specDeathEye.z) * f;
    }

    const cam = this.renderer.camera;
    cam.position.set(_camIdeal.x, _camIdeal.y, _camIdeal.z);
    const pitch = -Math.atan2(rise, Math.max(back, 1e-3));
    cam.rotation.set(pitch, yaw, 0);

    this.settleSpectatorCamera(_camIdeal, yaw, pitch);
  }

  /**
   * Riding a living teammate. First person, or over their shoulder — and in BOTH cases
   * the mouse owns the look direction, because a spectator who cannot turn their head
   * is a spectator watching a video.
   */
  private updateFollowCam(dt: number): void {
    const target = this.world.players[this.specTargetId];
    if (!target) {
      this.updateSpectateOrbit(dt);
      return;
    }

    eyePosition(target, _camEye);
    const yaw = this.input.yaw;
    const pitch = this.input.pitch;
    const cam = this.renderer.camera;

    if (this.specFirstPerson) {
      cam.position.set(_camEye.x, _camEye.y, _camEye.z);
      cam.rotation.set(pitch, yaw, 0);
      this.settleSpectatorCamera(_camEye, yaw, pitch);
      return;
    }

    // Ideal: behind the look direction, lifted, and offset to one shoulder so the body
    // never sits on the point the player is trying to read.
    dirFromYawPitch(_camDir, yaw, pitch);
    rightFromYaw(_camRight, yaw);
    _camIdeal.x = _camEye.x - _camDir.x * SPECTATE_DIST + _camRight.x * SPECTATE_SHOULDER;
    _camIdeal.y = _camEye.y - _camDir.y * SPECTATE_DIST + SPECTATE_RISE;
    _camIdeal.z = _camEye.z - _camDir.z * SPECTATE_DIST + _camRight.z * SPECTATE_SHOULDER;

    // Damp first, then sweep: damping a position that was already clamped by geometry
    // makes the camera crawl out of a wall, whereas clamping the damped one keeps it
    // out on every single frame.
    if (this.specCamValid) dampVec(this.specCamPos, this.specCamPos, _camIdeal, SPECTATE_DAMP, dt);
    else {
      copy(this.specCamPos, _camIdeal);
      this.specCamValid = true;
    }

    const hit = this.collision.traceSphere(_camEye, this.specCamPos, SPECTATE_CLEARANCE);
    if (hit.hit && !hit.startSolid) {
      const f = clamp01(hit.fraction);
      _camIdeal.x = _camEye.x + (this.specCamPos.x - _camEye.x) * f;
      _camIdeal.y = _camEye.y + (this.specCamPos.y - _camEye.y) * f;
      _camIdeal.z = _camEye.z + (this.specCamPos.z - _camEye.z) * f;
    } else {
      copy(_camIdeal, this.specCamPos);
    }

    cam.position.set(_camIdeal.x, _camIdeal.y, _camIdeal.z);
    cam.rotation.set(pitch, yaw, 0);
    this.settleSpectatorCamera(_camIdeal, yaw, pitch);
  }

  /**
   * Nobody is alive. The round is resolving itself either way, so the arena turns slowly
   * instead of the frame freezing — which is the difference between "the round is
   * ending" and "the game has crashed".
   */
  private updateSpectateOrbit(dt: number): void {
    this.specOrbitAngle += dt * SPECTATE_ORBIT_SPEED;

    const a = this.arena;
    const spanX = a.bounds.max.x - a.bounds.min.x;
    const spanZ = a.bounds.max.z - a.bounds.min.z;
    const radius = Math.max(spanX, spanZ) * SPECTATE_ORBIT_RADIUS;
    const cx = a.centerSpawn.x;
    const cz = a.centerSpawn.z;
    const height = a.centerSpawn.y + (a.bounds.max.y - a.centerSpawn.y) * SPECTATE_ORBIT_HEIGHT;

    const cam = this.renderer.camera;
    cam.position.set(cx + Math.cos(this.specOrbitAngle) * radius, height, cz + Math.sin(this.specOrbitAngle) * radius);
    // lookAt with a +Y up vector produces zero roll by construction.
    cam.lookAt(cx, a.centerSpawn.y + 2, cz);

    _camIdeal.x = cam.position.x;
    _camIdeal.y = cam.position.y;
    _camIdeal.z = cam.position.z;
    this.settleSpectatorCamera(_camIdeal, cam.rotation.y, cam.rotation.x);
  }

  /**
   * The three things every spectator camera has to say, in one place so none of them can
   * forget one: no roll, no speed feedback, resting FOV, and a listener that follows the
   * camera rather than the corpse.
   */
  private settleSpectatorCamera(pos: Vec3, yaw: number, pitch: number): void {
    this.renderer.setCameraRoll(0);
    this.renderer.setSpeed01(0);
    this.renderer.setTargetFov(this.fovBase);
    this.audio.setListener(pos, yaw, pitch);
  }

  /** Slow orbit of the arena behind the menu and the lobby. */
  private updateMenuCamera(dt: number): void {
    this.menuAngle += dt * MENU_ORBIT_SPEED;

    const a = this.arena;
    const spanX = a.bounds.max.x - a.bounds.min.x;
    const spanZ = a.bounds.max.z - a.bounds.min.z;
    const radius = Math.max(spanX, spanZ) * 0.46;
    const cx = a.centerSpawn.x;
    const cz = a.centerSpawn.z;
    const height = a.centerSpawn.y + (a.bounds.max.y - a.centerSpawn.y) * 0.5;

    const cam = this.renderer.camera;
    cam.position.set(cx + Math.cos(this.menuAngle) * radius, height, cz + Math.sin(this.menuAngle) * radius);
    cam.lookAt(cx, a.centerSpawn.y + 2, cz);

    // Keep the audio listener glued to the camera even here, so the arena's ambience is
    // spatialised behind the menu instead of collapsing to a point at the origin.
    _eye.x = cam.position.x;
    _eye.y = cam.position.y;
    _eye.z = cam.position.z;
    this.audio.setListener(_eye, cam.rotation.y, cam.rotation.x);

    this.renderer.setCameraRoll(0);
    this.renderer.setSpeed01(0);
    this.renderer.setTargetFov(this.fovBase);
  }

  // =========================================================================
  // Scene sync
  // =========================================================================

  private setArena(arena: ArenaData): void {
    this.arena = arena;
    this.collision = createCollisionWorld(arena);
    if (this.arenaName === arena.name && this.arenaGroup) return;

    if (this.arenaGroup) {
      this.renderer.scene.remove(this.arenaGroup);
      this.disposeArenaGroup(this.arenaGroup);
    }
    this.arenaGroup = buildArenaMesh(arena);
    this.arenaGroup.name = 'Arena';
    this.renderer.scene.add(this.arenaGroup);
    this.arenaName = arena.name;
  }

  /**
   * `buildArenaMesh` hangs its own teardown on the group it returns — it owns merged
   * geometry, generated textures and per-batch outline materials that nothing else will
   * ever collect. Dropping the group without calling it leaks the whole arena on every
   * menu -> match -> menu cycle.
   */
  private disposeArenaGroup(group: THREE.Group): void {
    const fn = (group.userData as { dispose?: () => void }).dispose;
    if (typeof fn === 'function') fn();
  }

  private syncScene(dt: number): void {
    this.sceneTimeSec += dt;
    const world = this.world;
    const hiddenId = this.hiddenRigId();
    const local = world.players[this.localId];
    const localTeam = local ? local.team : 0;

    // --- player rigs -------------------------------------------------------
    for (const id in world.players) {
      const p = world.players[id];
      let entry = this.rigs.get(id);
      if (entry && entry.team !== p.team) {
        this.renderer.scene.remove(entry.rig.object);
        entry.rig.dispose();
        entry = undefined;
        this.rigs.delete(id);
      }
      if (!entry) {
        const rig = new PlayerRig(p.team, p.name);
        this.renderer.scene.add(rig.object);
        entry = { rig, team: p.team };
        this.rigs.set(id, entry);
      }
      // The rig carries its own copy of the gun in the character's hand, but it cannot
      // know whether the chamber is loaded from `PlayerState` alone — and "who is armed"
      // is the single most important thing on screen (DESIGN.md §4). Tell it, every frame.
      const carried = heldGunOf(p, world);
      entry.rig.setHeldGunState(carried !== null && carried.chambered);
      entry.rig.update(p, dt, localTeam);
      // Never draw the body the camera lives inside — it would fill the screen. While
      // spectating over the shoulder that is nobody, so every rig stays visible.
      entry.rig.object.visible = id !== hiddenId;
    }

    for (const [id, entry] of this.rigs) {
      if (world.players[id]) continue;
      this.renderer.scene.remove(entry.rig.object);
      entry.rig.dispose();
      this.rigs.delete(id);
    }

    // --- guns --------------------------------------------------------------
    const liveGunIds = new Set<number>();
    for (const g of world.guns) {
      liveGunIds.add(g.id);
      let mesh = this.gunMeshes.get(g.id);
      if (!mesh) {
        mesh = buildGunMesh();
        mesh.name = `Gun${g.id}`;
        this.renderer.scene.add(mesh);
        this.gunMeshes.set(g.id, mesh);
      }

      // "Who is armed right now?" has to be answerable in one glance (DESIGN.md §4), so
      // team tint and the loaded/empty state are pushed the instant either changes.
      const styleKey = `${g.team}:${g.chambered ? 1 : 0}`;
      if (this.gunStyles.get(g.id) !== styleKey) {
        styleGun(mesh, g.team, g.chambered);
        this.gunStyles.set(g.id, styleKey);
      }

      // A held gun is drawn by its owner's rig (third person) or the view model (first
      // person), both of which parent it to an actual hand. The free-standing world mesh
      // exists only for a gun in flight or lying on the ground.
      const held = g.state === GunState.HeldChambered || g.state === GunState.HeldEmpty;
      if (held || g.state === GunState.Void) {
        mesh.visible = false;
        continue;
      }

      mesh.visible = true;
      mesh.position.set(g.pos.x, g.pos.y, g.pos.z);
      mesh.rotation.set(g.rot.x, g.rot.y, g.rot.z);
      animateGun(mesh, this.sceneTimeSec);
    }

    for (const [id, mesh] of this.gunMeshes) {
      if (liveGunIds.has(id)) continue;
      mesh.visible = false;
    }

    // --- view model --------------------------------------------------------
    // Only ever our own hands. A spectator borrows a teammate's camera, not their arms.
    const firstPerson = this.specMode === 'live' && local && local.healthState !== HealthState.Dead;
    if (firstPerson && local) {
      const g = heldGunOf(local, world);
      this.viewmodel.setGun(g !== null, g !== null && g.chambered, local.team);
      this.viewmodel.update(local, dt);
      this.viewmodel.object.visible = true;
    } else {
      this.viewmodel.object.visible = false;
    }
  }

  private clearMatchScene(): void {
    for (const entry of this.rigs.values()) {
      this.renderer.scene.remove(entry.rig.object);
      entry.rig.dispose();
    }
    this.rigs.clear();

    for (const mesh of this.gunMeshes.values()) mesh.visible = false;
    this.gunStyles.clear();

    this.viewmodel.object.visible = false;

    // Leaving a match must not carry a spectator camera into the next one.
    this.exitSpectate();
    this.lastCmdButtons = 0;

    this.interp.clear();
    this.clock.reset();
    this.predictor?.reset();
    this.netWorld = createEmptyWorldState();
    this.world = createEmptyWorldState();
    this.pendingCmds.length = 0;
    this.accumulator = 0;
    this.lastSnapshotAtMs = 0;
    this.lastLocalHealth = HealthState.Dead;
  }

  // =========================================================================
  // Events
  // =========================================================================

  /**
   * Every gameplay event gets a visual *and* an audible response (DESIGN.md §7). The three
   * consumers are independent, so a throw in one of them can never silence the others.
   */
  private routeEvent(e: GameEvent): void {
    try {
      this.vfx.handle(e, this.world, this.localId);
    } catch (err) {
      this.warn('vfx', err);
    }
    try {
      this.audio.handle(e, this.world, this.localId);
    } catch (err) {
      this.warn('audio', err);
    }
    try {
      this.ui.handle(e, this.world, this.localId);
    } catch (err) {
      this.warn('ui', err);
    }
    this.localFeedback(e);
  }

  /** Screen-space feel that only the renderer and the view model can produce. */
  private localFeedback(e: GameEvent): void {
    const me = this.localId;
    switch (e.t) {
      case 'shot':
        if (e.player === me) {
          this.viewmodel.playFire();
          this.addShake(FIRE_SHAKE);
        }
        return;

      case 'dryfire':
        if (e.player === me) this.viewmodel.playDryFire();
        return;

      case 'throw':
        if (e.player === me) this.viewmodel.playThrow();
        return;

      case 'catch':
        if (e.player === me) this.viewmodel.playCatch();
        return;

      case 'pulse':
        if (e.player === me) this.viewmodel.playPulse();
        return;

      // The impact frame in the killer's colour is deliberately NOT raised here: it is
      // owned by VfxSystem.onKill (already gated on us being in the fight) and reaches
      // the renderer through `vfx.hooks.inkSplash`. Splashing here as well would restart
      // the two-frame hold on top of itself. All this case does is remember who to point
      // the death camera at.
      case 'kill':
        if (e.victim === me) this.noteLocalDeath(e.killer);
        return;

      case 'land': {
        if (e.player !== me) return;
        // A plain jump lands at JUMP_VELOCITY and should not shake at all; only the drops
        // that actually hurt do. LAND_SHAKE_SCALE is trauma per m/s of excess impact.
        const excess = e.speed - JUMP_VELOCITY;
        if (excess > 0) this.addShake(clamp01(excess * LAND_SHAKE_SCALE));
        return;
      }

      default:
        return;
    }
  }

  private warn(where: string, err: unknown): void {
    // One misbehaving consumer must not take the frame loop down with it.
    console.error(`[onetap] ${where} handler failed`, err);
  }

  // =========================================================================
  // Input callbacks
  // =========================================================================

  private wakeAudio(): void {
    void this.audio.resume().catch(() => undefined);
  }

  private handleLockChange(locked: boolean): void {
    if (locked) {
      this.uiPauseOpen = false;
      this.cancelLockRetry();
      this.hideShell();
      return;
    }
    if (this.screen !== 'match') return;
    // Escape means the player asked for the pause panel and the UI is already showing it.
    // Anything else — alt-tab, a lost window, the start of the match — is ours to explain.
    if (this.uiPauseOpen && performance.now() - this.lastEscapeAtMs < PAUSE_ATTRIBUTION_MS) return;
    this.showLockPrompt();
  }

  private handleLockBlocked(reason: string, retryInMs: number): void {
    if (this.screen !== 'match') return;
    // The browser rate-limits pointer-lock re-entry for over a second after Escape. The
    // player already asked to resume, so finish the job for them when the window opens
    // rather than making them click into a wall — and show the prompt meanwhile so there
    // is always something to click if the retry is refused too.
    this.scheduleLockRetry(retryInMs);
    this.showLockPrompt();
    const wait = Math.ceil(retryInMs / 100) / 10;
    this.setShellMessage(retryInMs > 0 ? `${reason} (${wait.toFixed(1)}s)` : reason);
  }

  /** Ask for the pointer now; if the browser is cooling down, ask again when it is not. */
  private acquireLock(): void {
    this.cancelLockRetry();
    const cooldown = this.input.lockCooldownMs();
    if (cooldown > 0) {
      this.scheduleLockRetry(cooldown);
      this.showLockPrompt();
      return;
    }
    this.input.requestLock();
  }

  private scheduleLockRetry(delayMs: number): void {
    this.cancelLockRetry();
    if (delayMs <= 0) return;
    this.lockRetryTimer = setTimeout(() => {
      this.lockRetryTimer = null;
      if (this.disposed || this.screen !== 'match') return;
      if (this.input.isPointerLocked() || this.uiPauseOpen) return;
      this.input.requestLock();
    }, delayMs + LOCK_RETRY_PAD_MS);
  }

  private cancelLockRetry(): void {
    if (this.lockRetryTimer === null) return;
    clearTimeout(this.lockRetryTimer);
    this.lockRetryTimer = null;
  }

  private handleInputAction(action: string, down: boolean): void {
    if (action === 'menu' && down) {
      // Escape only ever *releases* the mouse. Leaving a match is an explicit button on
      // the pause panel — a stray key press must never drop a teammate out of a round.
      if (this.input.isPointerLocked()) this.input.releaseLock();
      return;
    }
    // The scoreboard is a HUD concern; the UI reads it out of the held state each frame
    // via `updateHud`, so nothing else is needed here beyond the audible confirmation.
    if (action === 'scoreboard' && down) this.audio.ui('hover');
  }

  // =========================================================================
  // Shell: pause / click-to-play overlay and the dev readout
  // =========================================================================

  private buildShell(): void {
    if (document.getElementById('onetap-shell-style') === null) {
      const style = document.createElement('style');
      style.id = 'onetap-shell-style';
      style.textContent = SHELL_CSS;
      document.head.appendChild(style);
    }

    const root = document.createElement('div');
    root.className = 'ot-shell';
    root.hidden = true;
    root.innerHTML = `
      <div class="ot-shell__panel" role="dialog" aria-modal="true" aria-label="Paused">
        <p class="ot-shell__eyebrow">ONE TAP</p>
        <h2 class="ot-shell__title">CLICK TO PLAY</h2>
        <p class="ot-shell__msg"></p>
        <div class="ot-shell__actions">
          <button type="button" class="ot-shell__btn ot-shell__btn--primary" data-act="resume">RESUME</button>
          <button type="button" class="ot-shell__btn" data-act="leave">LEAVE MATCH</button>
        </div>
        <ul class="ot-shell__keys"></ul>
      </div>`;

    this.shellTitle = root.querySelector('.ot-shell__title');
    this.shellMessage = root.querySelector('.ot-shell__msg');
    this.shellKeys = root.querySelector('.ot-shell__keys');
    this.shellResume = root.querySelector('[data-act="resume"]');
    this.shellLeave = root.querySelector('[data-act="leave"]');

    // Clicking anywhere on the backdrop resumes: at this point the player wants to play,
    // and demanding they hit a specific rectangle is friction for no reason.
    root.addEventListener('pointerdown', (e) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('.ot-shell__btn')) return;
      e.preventDefault();
      this.resumeFromShell();
    });
    this.shellResume?.addEventListener('click', (e) => {
      e.preventDefault();
      this.audio.ui('click');
      this.resumeFromShell();
    });
    this.shellLeave?.addEventListener('click', (e) => {
      e.preventDefault();
      this.leave();
    });

    document.body.appendChild(root);
    this.shellRoot = root;

    const dev = document.createElement('div');
    dev.className = 'ot-dev';
    dev.hidden = true;
    dev.innerHTML = '<pre></pre>';
    this.devText = dev.querySelector('pre');
    document.body.appendChild(dev);
    this.devRoot = dev;
  }

  private resumeFromShell(): void {
    if (this.contextLost) return;
    this.wakeAudio();
    this.uiPauseOpen = false;
    this.acquireLock();
  }

  private showLockPrompt(): void {
    if (!this.shellRoot) return;
    // A pad player never needs the mouse; showing them a click-to-play wall would be
    // an interface they cannot dismiss.
    if (this.input.padActive) {
      this.hideShell();
      return;
    }
    this.shellMode = 'lock';
    this.shellRoot.hidden = false;
    if (this.shellTitle) this.shellTitle.textContent = 'CLICK TO PLAY';
    if (this.shellLeave) this.shellLeave.hidden = false;
    if (this.shellResume) this.shellResume.hidden = false;

    const cooldown = this.input.lockCooldownMs();
    this.setShellMessage(
      cooldown > 0
        ? this.input.lockBlockedReason || 'Mouse capture is cooling down.'
        : 'Press ESC at any time to release the mouse.',
    );
    this.renderShellKeys();
  }

  private showNotice(title: string, message: string, showActions: boolean): void {
    if (!this.shellRoot) return;
    this.shellMode = 'notice';
    this.shellRoot.hidden = false;
    if (this.shellTitle) this.shellTitle.textContent = title;
    this.setShellMessage(message);
    if (this.shellResume) this.shellResume.hidden = !showActions;
    if (this.shellLeave) this.shellLeave.hidden = !showActions;
    if (this.shellKeys) this.shellKeys.innerHTML = '';
  }

  private hideShell(): void {
    this.shellMode = 'hidden';
    if (this.shellRoot) this.shellRoot.hidden = true;
  }

  private setShellMessage(text: string): void {
    if (this.shellMessage) this.shellMessage.textContent = text;
  }

  /** The control crib sheet, generated from the live bindings so it can never go stale. */
  private renderShellKeys(): void {
    if (!this.shellKeys) return;
    const b = this.input.bindings;
    const rows: [string, string][] = [
      ['MOVE', `${describeBinding(b.moveForward)} / ${describeBinding(b.moveLeft)} / ${describeBinding(b.moveBack)} / ${describeBinding(b.moveRight)}`],
      ['JUMP', describeBinding(b.jump)],
      ['SLIDE', describeBinding(b.crouch)],
      ['DASH', describeBinding(b.dash)],
      ['GRAPPLE', describeBinding(b.grapple)],
      ['FIRE', describeBinding(b.fire)],
      ['THROW (HOLD TO CHARGE)', describeBinding(b.throwGun)],
      ['CATCH RAW (HOLD)', describeBinding(b.catchRaw)],
      ['PULSE', describeBinding(b.pulse)],
      ['SCOREBOARD', describeBinding(b.scoreboard)],
    ];
    this.shellKeys.innerHTML = rows
      .map(([k, v]) => `<li><span>${k}</span><b>${escapeHtml(v)}</b></li>`)
      .join('');
  }

  private setDevOverlay(on: boolean): void {
    this.devVisible = on;
    if (this.devRoot) this.devRoot.hidden = !on;
    if (this.settings.get().showStats !== on) this.settings.patch({ showStats: on });
  }

  private updateStats(nowMs: number): void {
    if (nowMs - this.statsAtMs < STATS_INTERVAL_MS) return;
    this.statsAtMs = nowMs;

    const pingMs = this.conn ? Math.round(this.conn.latencyMs * 2) : 0;
    this.ui.setStats(Math.round(this.fps), pingMs);

    if (!this.devVisible || !this.devText) return;

    const info = this.renderer.gl.info;
    const predictor = this.predictor;
    const lines = [
      `fps        ${this.fps.toFixed(0).padStart(5)}   frame ${this.frameMs.toFixed(2)} ms`,
      `quality    ${this.renderer.quality}${this.renderer.autoQuality ? ' (auto)' : ' (pinned)'}`,
      `draws      ${info.render.calls}   tris ${info.render.triangles}   progs ${info.programs?.length ?? 0}`,
      `net        ${this.connStatus}   rtt ${pingMs} ms   offset ${this.conn ? this.conn.serverTimeOffsetMs.toFixed(0) : '-'} ms`,
      `room       ${this.roomCode || '-'}   tick ${this.world.tick}   phase ${PHASE_NAMES[this.world.match.phase] ?? this.world.match.phase}`,
      `clock      ${this.clock.ready ? 'locked' : 'priming'}   accum ${(this.accumulator * 1000).toFixed(1)} ms`,
      `predict    err ${predictor ? predictor.lastCorrectionDistance.toFixed(3) : '-'} m   corrections ${predictor ? predictor.corrections : '-'}   pending ${predictor ? predictor.pending : '-'}`,
      `entities   rigs ${this.rigs.size}   guns ${this.gunMeshes.size}   players ${Object.keys(this.world.players).length}`,
    ];
    this.devText.textContent = lines.join('\n');
  }
}

// ---------------------------------------------------------------------------

/** The first binding for an action, as a single short key cap. */
function primaryKey(codes: readonly InputCode[]): string {
  return codes.length > 0 ? describeBinding([codes[0]!]) : describeBinding(codes);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

/**
 * The shell's styles are scoped under `ot-` and injected here rather than living in the
 * UI module, because the pause overlay has to survive the UI being mid-transition — and
 * because it is the thing that tells the player what happened when something breaks.
 */
const SHELL_CSS = `
/* Sunlit Cel (DESIGN.md §4): frosted glass over a bright scene.
   No accent sidebars, no offset colour shadows, no heavy display type. */
.ot-shell {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: grid;
  place-items: center;
  background: rgba(250, 250, 248, 0.58);
  backdrop-filter: saturate(180%) blur(16px);
  color: #2a3242;
  font-family: 'Helvetica Neue', Inter, system-ui, -apple-system, Arial, sans-serif;
  cursor: pointer;
  user-select: none;
}
.ot-shell[hidden] { display: none; }
.ot-shell__panel {
  min-width: min(30rem, 86vw);
  max-width: 92vw;
  padding: 2.75rem 3rem;
  text-align: center;
  border: 1px solid rgba(42, 50, 66, 0.10);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 6px 16px rgba(42, 50, 66, 0.08), 0 28px 64px rgba(42, 50, 66, 0.10);
}
.ot-shell__eyebrow {
  margin: 0 0 0.7rem;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.42em;
  color: #8b95a6;
  text-transform: uppercase;
}
.ot-shell__title {
  margin: 0 0 0.8rem;
  font-size: clamp(1.7rem, 5vw, 2.8rem);
  font-weight: 200;
  letter-spacing: 0.1em;
  line-height: 1.05;
  color: #2a3242;
}
.ot-shell__msg {
  margin: 0 0 1.8rem;
  min-height: 1.2em;
  font-size: 0.8rem;
  font-weight: 400;
  letter-spacing: 0.1em;
  color: #5a6579;
  text-transform: uppercase;
}
.ot-shell__actions { display: flex; gap: 0.75rem; justify-content: center; flex-wrap: wrap; }
.ot-shell__btn {
  appearance: none;
  cursor: pointer;
  padding: 0.75rem 1.6rem;
  font: inherit;
  font-size: 0.74rem;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #2a3242;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(42, 50, 66, 0.16);
  border-radius: 999px;
  transition: transform 120ms cubic-bezier(0.16, 1, 0.3, 1), background-color 120ms ease,
    border-color 120ms ease, color 120ms ease, box-shadow 120ms ease;
}
.ot-shell__btn[hidden] { display: none; }
.ot-shell__btn:hover {
  border-color: rgba(42, 50, 66, 0.28);
  background: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(42, 50, 66, 0.07), 0 10px 28px rgba(42, 50, 66, 0.07);
}
.ot-shell__btn:active { transform: translateY(0); box-shadow: none; }
.ot-shell__btn:focus-visible { outline: 2px solid #0a84ff; outline-offset: 3px; }
.ot-shell__btn--primary { background: #0a84ff; border-color: #0a84ff; color: #ffffff; }
.ot-shell__btn--primary:hover { background: #339bff; border-color: #339bff; color: #ffffff; }
.ot-shell__keys {
  margin: 2rem 0 0;
  padding: 1.4rem 0 0;
  list-style: none;
  border-top: 1px solid rgba(42, 50, 66, 0.10);
  display: grid;
  gap: 0.35rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
}
.ot-shell__keys:empty { display: none; }
.ot-shell__keys li { display: flex; justify-content: space-between; gap: 1.5rem; color: #8b95a6; }
.ot-shell__keys b { color: #2a3242; font-weight: 500; }
.ot-dev {
  position: fixed;
  top: 0.6rem;
  left: 0.6rem;
  z-index: 95;
  pointer-events: none;
  padding: 0.6rem 0.8rem;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: saturate(180%) blur(12px);
  border: 1px solid rgba(42, 50, 66, 0.10);
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(42, 50, 66, 0.06), 0 2px 8px rgba(42, 50, 66, 0.04);
  color: #2a3242;
}
.ot-dev[hidden] { display: none; }
.ot-dev pre {
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.68rem;
  line-height: 1.45;
  white-space: pre;
}
`;
